import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MakeupSchedule } from '@/types';
import { formatDday } from '@/lib/datetime';
import QuickMakeupModal from '@/pages/dashboard/components/QuickMakeupModal';

export interface UpcomingMakeup extends MakeupSchedule {
  studentName: string;
}

interface StudentOption {
  id: string;
  name: string;
  status?: string | null;
}

interface UpcomingMakeupCardProps {
  makeups: UpcomingMakeup[];
  students: StudentOption[];
  onAdded: () => void;
}

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function weekdayLabel(dateStr: string): string {
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return WEEKDAYS[d.getDay()];
}

function dateBadge(day: number, month: number) {
  return `${month}/${day}`;
}

function ddayBadgeClass(tone: 'past' | 'soon' | 'far'): string {
  if (tone === 'soon') return 'bg-accent-100 text-accent-800';
  if (tone === 'past') return 'bg-background-200 text-foreground-500';
  return 'bg-secondary-100 text-secondary-900';
}

export default function UpcomingMakeupCard({ makeups, students, onAdded }: UpcomingMakeupCardProps) {
  const [open, setOpen] = useState(false);

  // 수업이 종료된 학생은 새 보충 일정의 학생 선택지에서 제외해요.
  const selectableStudents = useMemo(
    () => students.filter((s) => s.status !== 'ended'),
    [students],
  );

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-100 text-accent-700">
            <i className="ri-calendar-schedule-line"></i>
          </span>
          <h2 className="text-base font-bold text-foreground-950">다가오는 보충 수업</h2>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-semibold text-background-50 transition hover:bg-accent-600 whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          보충 일정 추가
        </button>
      </div>

      {makeups.length === 0 ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-lg border border-dashed border-background-300 py-10">
          <i className="ri-calendar-check-line text-2xl text-foreground-400"></i>
          <p className="text-sm text-foreground-600">예정된 보충 수업이 없어요.</p>
        </div>
      ) : (
        <div className="mt-5 flex flex-col gap-3">
          {makeups.map((m) => {
            const d = new Date(`${m.scheduled_date}T00:00:00`);
            const dday = formatDday(m.scheduled_date);
            const valid = !Number.isNaN(d.getTime());
            return (
              <Link
                key={m.id}
                to={`/students/${m.student_id}`}
                className="flex items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-4 py-3 transition hover:border-accent-300"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <div className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                    <span className="text-[10px] font-semibold leading-none">
                      {valid ? dateBadge(d.getDate(), d.getMonth() + 1) : '-'}
                    </span>
                    {valid && (
                      <span className="mt-0.5 text-[10px] leading-none">
                        {weekdayLabel(m.scheduled_date)}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-foreground-900">
                      {m.studentName}
                      <span className="ml-1.5 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-semibold text-accent-800">
                        {m.reason || '보충'}
                      </span>
                    </p>
                    <p className="mt-0.5 truncate text-xs text-foreground-500">
                      {m.note || '보충 범위 미정'}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  {dday && (
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${ddayBadgeClass(
                        dday.tone,
                      )}`}
                    >
                      {dday.label}
                    </span>
                  )}
                  <span className="text-[11px] text-foreground-500 whitespace-nowrap">
                    {m.scheduled_date}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      <QuickMakeupModal
        open={open}
        onClose={() => setOpen(false)}
        students={selectableStudents}
        onAdded={onAdded}
      />
    </section>
  );
}